'use client';

import { useCallback, useEffect, useState } from 'react';
import { Plus, X } from 'lucide-react';
import { useBlockModalLedBarStore } from '@/stores/useBlockModalLedBarStore';
import { useBlockModalStore } from '@/stores/useBlockModalStore';
import IconAdaptive from '@/components/IconAdaptive';
import ImageAdaptive from '@/components/ImageAdaptive';
import { CATEGORIES } from '@/(routers)/boards/editor/block/definitions/categories';
import type { LedBarStepData } from '@/(routers)/boards/editor/block/definitions/fields/fieldLedBar';

const LED_COUNT = 10;

// LED番号(1〜10)に対応する色 index 0 = LED1
const LED_COLORS_ACTIVE: Record<number, string> = {
    0: '#1976D2', // 1: 青
    1: '#388E3C', // 2: 緑
    2: '#388E3C', // 3: 緑
    3: '#388E3C', // 4: 緑
    4: '#388E3C', // 5: 緑
    5: '#F57C00', // 6: オレンジ
    6: '#F57C00', // 7: オレンジ
    7: '#F57C00', // 8: オレンジ
    8: '#E53935', // 9: 赤
    9: '#E53935', // 10: 赤
};

const LED_COLORS_INACTIVE: Record<number, string> = {
    0: '#0d2a4a', // 1: 青暗
    1: '#1a3a1a', // 2: 緑暗
    2: '#1a3a1a', // 3: 緑暗
    3: '#1a3a1a', // 4: 緑暗
    4: '#1a3a1a', // 5: 緑暗
    5: '#3d2200', // 6: オレンジ暗
    6: '#3d2200', // 7: オレンジ暗
    7: '#3d2200', // 8: オレンジ暗
    8: '#4a0f0f', // 9: 赤暗
    9: '#4a0f0f', // 10: 赤暗
};

const DURATIONS = [
    { label: 'もっと短い', value: 75 },
    { label: '短い', value: 150 },
    { label: 'ふつうの長さ', value: 300 },
    { label: '長い', value: 450 },
    { label: 'もっと長い', value: 600 },
];

function getLedCountColor(count: number): string {
    if (count === 0) return 'rgba(255,255,255,0.3)';
    const t = (count - 1) / 9;
    const r = Math.round(120 + (229 - 120) * t);
    const g = Math.round(20 + (57 - 20) * t);
    const b = Math.round(20 + (53 - 20) * t);
    return `rgb(${r},${g},${b})`;
}

export default function LedBar() {
    const { closeModal, setOnConfirm } = useBlockModalStore();
    const { fieldRef } = useBlockModalLedBarStore();
    const [steps, setSteps] = useState<LedBarStepData[]>(fieldRef?.getSteps() ?? []);

    const updateStep = (index: number, updates: Partial<LedBarStepData>) => {
        setSteps((prev) => prev.map((s, i) => (i === index ? { ...s, ...updates } : s)));
    };

    const toggleLed = (stepIndex: number, ledIndex: number) => {
        setSteps((prev) =>
            prev.map((s, i) => {
                if (i !== stepIndex) return s;
                const leds = [...s.leds];
                leds[ledIndex] = !leds[ledIndex];
                return { ...s, leds };
            }),
        );
    };

    const addStep = () => {
        setSteps((prev) => [...prev, { leds: Array(LED_COUNT).fill(false), duration: 300 }]);
    };

    const deleteStep = (index: number) => {
        setSteps((prev) => prev.filter((_, i) => i !== index));
    };

    const handleOk = useCallback(() => {
        fieldRef?.setSteps(steps);
        closeModal();
    }, [steps, fieldRef, closeModal]);

    useEffect(() => {
        setOnConfirm(handleOk);
    }, [handleOk, setOnConfirm]);

    return (
        <div className="flex flex-col h-full bg-color-block-action-modal-bg rounded-2xl overflow-hidden">
            <div className="flex items-center px-3 h-[50px] bg-color-block-action-modal-header flex-shrink-0">
                <span className="text-xl mr-2">
                    <ImageAdaptive src="/images/block/led_bar.svg" baseWidth={40} />
                </span>
                <span className="text-color-white-base font-semibold text-sm">LEDバーの光設定</span>
            </div>

            <div className="flex-1 overflow-y-auto p-4 flex flex-col gap-2 min-h-0">
                {steps.map((step, i) => {
                    const onCount = step.leds.filter(Boolean).length;
                    const isOff = onCount === 0;
                    return (
                        <div
                            key={i}
                            className="flex items-center gap-2 bg-color-block-action-modal-row-bg rounded-lg px-3 py-2 flex-shrink-0"
                        >
                            <span className="text-color-block-action-modal-text-index text-xs w-4 text-center flex-shrink-0">
                                {i + 1}
                            </span>

                            <div className="flex gap-1 flex-shrink-0">
                                {Array.from({ length: LED_COUNT }, (_, i2) => {
                                    const li = LED_COUNT - 1 - i2;
                                    const active = step.leds[li];
                                    const bgColor = active ? LED_COLORS_ACTIVE[li] : LED_COLORS_INACTIVE[li];
                                    return (
                                        <button
                                            key={li}
                                            onClick={() => toggleLed(i, li)}
                                            className="w-7 h-7 rounded text-xs font-semibold transition-all"
                                            style={{
                                                backgroundColor: bgColor,
                                                color: active ? 'rgba(255,255,255,0.95)' : 'rgba(255,255,255,0.3)',
                                                border: active
                                                    ? `1px solid ${LED_COLORS_ACTIVE[li]}`
                                                    : '1px solid rgba(255,255,255,0.08)',
                                                boxShadow: active ? `0 0 6px ${LED_COLORS_ACTIVE[li]}88` : 'none',
                                            }}
                                        />
                                    );
                                })}
                            </div>

                            <span
                                className="text-xs font-bold w-6 text-center flex-shrink-0"
                                style={{ color: isOff ? 'rgba(255,255,255,0.3)' : getLedCountColor(onCount) }}
                            >
                                {isOff ? '消' : onCount}
                            </span>

                            <select
                                value={step.duration}
                                onChange={(e) => updateStep(i, { duration: Number(e.target.value) })}
                                className="flex-1 bg-color-block-action-modal-button-bg text-color-white-base border-none rounded text-xs px-2 py-1"
                            >
                                {DURATIONS.map((d) => (
                                    <option key={d.value} value={d.value}>
                                        {d.label}
                                    </option>
                                ))}
                            </select>

                            <button
                                onClick={() => deleteStep(i)}
                                className="text-color-block-action-modal-text-index hover:text-color-block-action-modal-header text-sm px-1 flex-shrink-0"
                            >
                                <IconAdaptive Icon={X} strokeWidth={2} baseSize={16} />
                            </button>
                        </div>
                    );
                })}
            </div>

            <div className="flex justify-between items-center gap-2 px-5 py-3 border-t border-color-block-action-modal-border flex-shrink-0">
                <button
                    onClick={addStep}
                    className="flex items-center pl-4 pr-5 py-2 bg-color-block-action-modal-button-bg border border-dashed rounded-lg text-xs hover:border-color-block-action-modal-header hover:text-color-block-action-modal-header transition-colors animate-pulse"
                    style={{ borderColor: CATEGORIES.action.color, color: CATEGORIES.action.color }}
                >
                    <IconAdaptive Icon={Plus} strokeWidth={2} baseSize={16} className="mr-1" />
                    色を追加
                </button>
                <div className="flex gap-2">
                    <button
                        onClick={closeModal}
                        className="px-4 py-2 bg-color-block-action-modal-cancel text-color-block-action-modal-text-gray rounded-lg text-sm"
                    >
                        キャンセル
                    </button>
                    <button
                        onClick={handleOk}
                        className="px-6 py-2 bg-color-block-action-modal-ok text-color-white-base rounded-lg text-sm font-semibold hover:bg-color-block-action-modal-ok-hover transition-colors"
                    >
                        OK
                    </button>
                </div>
            </div>
        </div>
    );
}
